import React, { useState } from 'react';
import { cn } from '../../../utils/cn';
import type { InvoiceRecord } from '../../cashflow/types';
import { postFeedback } from '../data';
import { FoldRow } from '../types';

/**
 * Per-row feedback on the AI analysis → POST /recon/feedback.
 * The submission auto-attaches a full row snapshot in `context` (free-form
 * JSON, stored as-is) so the AI team never has to re-join data. created_by
 * is recorded server-side from the auth token.
 */

export interface FeedbackTarget {
	row: FoldRow;
	depthRow?: InvoiceRecord | null;
	threadId?: string | null;
	surface: 'list' | 'drawer';
}

interface Props {
	target: FeedbackTarget | null;
	/** active list filter label, recorded into context for "where did this come from". */
	activeFilter?: string | null;
	onClose: () => void;
	onSubmitted: (invoiceNumber: string) => void;
}

const TYPES: Array<{ value: string; label: string; positive?: boolean }> = [
	{ value: 'wrong_verdict', label: 'Verdict is wrong' },
	{ value: 'wrong_category', label: 'Wrong stream — vendor / payable / internal, not receivable' },
	{ value: 'not_relevant', label: 'Not relevant — internal email, no vendor involved' },
	{ value: 'wrong_owner', label: 'Wrong owner attributed' },
	{ value: 'wrong_link', label: 'Thread is about a different invoice' },
	{ value: 'wrong_amount', label: 'Amount is wrong' },
	{ value: 'confirmed_correct', label: 'Confirmed correct — the AI got it right', positive: true },
];

const SUGGESTED = ['DISPUTED', 'LEAK', 'OPEN', 'PAID_UNPROVEN', 'RECONCILED', 'CANCELLED'];

export const FeedbackDialog: React.FC<Props> = ({ target, activeFilter, onClose, onSubmitted }) => {
	const [type, setType] = useState<string>('');
	const [suggested, setSuggested] = useState<string>('');
	const [note, setNote] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (!target) return null;
	const { row, depthRow, threadId, surface } = target;

	const reset = () => {
		setType('');
		setSuggested('');
		setNote('');
		setError(null);
	};

	const submit = async () => {
		if (!type || sending) return;
		setSending(true);
		setError(null);
		try {
			await postFeedback({
				invoice_number: row.invoice_number,
				thread_id: threadId ?? undefined,
				feedback_type: type,
				suggested_class: type === 'wrong_verdict' && suggested ? suggested : undefined,
				note: note.trim() || undefined,
				context: {
					shown_recon_class: row.recon_class ?? null,
					shown_leak_class: row.leak_class,
					state: row.state,
					customer: row.customer_name,
					amount: Number(row.amount_at_stake) || 0,
					owner: row.owner ?? null,
					next_action: row.next_action ?? null,
					n_threads: row.n_threads,
					has_contradiction: !!row.has_contradiction,
					zoho_status: depthRow?.z?.status ?? null,
					days_overdue: depthRow?.z?.od ?? null,
					emails: depthRow?.c?.emails ?? null,
					quiet_days: depthRow?.c?.quiet ?? null,
					kam: depthRow?.kam ?? null,
					aging: depthRow?.aging ?? null,
					surface,
					active_filter: activeFilter ?? null,
					submitted_from: 'cashflow-v2',
				},
			});
			onSubmitted(row.invoice_number);
			reset();
			onClose();
		} catch (e) {
			setError((e as Error)?.message || 'Could not send — try again');
		} finally {
			setSending(false);
		}
	};

	return (
		<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/30 [font-family:Inter,sans-serif]' onClick={onClose}>
			<div
				className='w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-[0_4px_8px_rgba(0,0,0,0.05)]'
				onClick={e => e.stopPropagation()}
			>
				<div className='border-b border-slate-100 px-5 py-3.5'>
					<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>Flag this analysis</div>
					<div className='mt-0.5 flex items-baseline gap-2'>
						<span className='font-mono text-[13px] font-medium text-slate-800'>{row.invoice_number}</span>
						<span className='truncate text-[13px] text-slate-500'>{row.customer_name}</span>
					</div>
				</div>

				<div className='space-y-1 px-5 py-3.5'>
					{TYPES.map(t => (
						<label
							key={t.value}
							className={cn(
								'flex cursor-pointer items-center gap-2.5 rounded px-2.5 py-1.5 text-[13px] transition-colors',
								type === t.value ? (t.positive ? 'bg-emerald-600/10 text-emerald-800' : 'bg-slate-100 text-slate-800') : 'text-slate-600 hover:bg-slate-50'
							)}
						>
							<input type='radio' name='fb-type' className='accent-[#242f41]' checked={type === t.value} onChange={() => setType(t.value)} />
							{t.label}
						</label>
					))}

					{type === 'wrong_verdict' && (
						<div className='flex items-center gap-2 pl-2.5 pt-1'>
							<span className='text-[13px] text-slate-500'>Should be:</span>
							<select
								value={suggested}
								onChange={e => setSuggested(e.target.value)}
								className='rounded border border-slate-300 px-2 py-1 text-[13px] text-slate-700 focus:border-slate-600 focus:outline-none'
							>
								<option value=''>— pick one —</option>
								{SUGGESTED.map(s => (
									<option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
								))}
							</select>
						</div>
					)}

					<textarea
						value={note}
						onChange={e => setNote(e.target.value)}
						placeholder='Optional note — anything that helps the analysis improve'
						rows={2}
						className='mt-2 w-full rounded border border-slate-300 px-2.5 py-1.5 text-[13px] text-slate-700 placeholder:text-slate-400 focus:border-slate-600 focus:outline-none'
					/>

					{error && <div className='rounded bg-[#ba1a1a]/10 px-2.5 py-1.5 text-[13px] text-[#ba1a1a]'>{error}</div>}
				</div>

				<div className='flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3'>
					<button onClick={onClose} className='rounded px-3 py-1.5 text-[13px] font-medium text-slate-500 hover:bg-slate-100'>
						Cancel
					</button>
					<button
						onClick={submit}
						disabled={!type || sending}
						className={cn(
							'rounded px-4 py-1.5 text-[13px] font-semibold text-white transition-colors',
							!type || sending ? 'cursor-default bg-slate-300' : 'bg-[#242f41] hover:bg-[#3a4558]'
						)}
					>
						{sending ? 'Sending…' : 'Send feedback'}
					</button>
				</div>
			</div>
		</div>
	);
};
